import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error fetching products:', error);
      }
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24 md:py-32">
      <SEO
        title="Products | BCM-95® Curcumin Supplements | IIY Phytoventra"
        description="Shop BCM-95® curcumin supplements from IIY Phytoventra. 100% Curcugreen®, allergen-free, vegan capsules — UK delivery included."
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <span className="text-sm font-medium tracking-widest uppercase text-primary mb-4 block">
            Our Products
          </span>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 font-heading" data-testid="products-title">
            Clinically Studied Curcumin
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-6">
            Clinically validated formulations backed by science and trusted by thousands
          </p>
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-6 py-3 rounded-full">
            <CheckCircle className="w-5 h-5" />
            <span className="font-semibold">In Stock — Order Online Now</span>
          </div>
        </motion.div>

        {products.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-xl text-muted-foreground">No products available at the moment.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
            {products.map((product, index) => {
              const isComingSoon = product.status === 'coming_soon' || product.available_for_purchase === false;
              const linkTarget = product.page_url || (!isComingSoon ? `/products/${product.id}` : null);
              return (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`bg-white rounded-3xl overflow-hidden shadow-soft transition-all duration-500 group relative ${isComingSoon ? 'opacity-95' : 'hover:shadow-hover'}`}
                data-testid={`product-card-${index}`}
              >
                {isComingSoon && (
                  <div className="absolute top-4 right-4 z-10 bg-amber-500 text-black text-[10px] font-bold tracking-[0.25em] uppercase px-3 py-1.5 rounded-full shadow-lg" data-testid={`coming-soon-badge-${index}`}>
                    Coming Soon
                  </div>
                )}
                <div
                  onClick={() => linkTarget && navigate(linkTarget)}
                  className={linkTarget ? 'cursor-pointer' : 'cursor-default'}
                >
                  <div className="aspect-square overflow-hidden bg-gradient-to-br from-primary/10 to-white relative">
                    <img
                      src={product.image}
                      alt={product.name}
                      className={`w-full h-full object-cover transition-transform duration-500 ${isComingSoon ? 'grayscale-[40%]' : 'group-hover:scale-110'}`}
                    />
                    {isComingSoon && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 backdrop-blur-[1px]">
                        <div className="bg-white/95 px-5 py-2 rounded-full shadow-lg">
                          <span className="text-sm font-bold text-amber-700 tracking-wide">Launching Soon</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="p-8">
                    <h3 className={`text-2xl font-bold mb-3 font-heading transition-colors ${isComingSoon ? '' : 'group-hover:text-primary'}`} data-testid={`product-name-${index}`}>
                      {product.name}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {product.description}
                    </p>

                    {product.features && product.features.length > 0 && (
                      <div className="space-y-2 mb-4">
                        {product.features.slice(0, 6).map((feature) => (
                          <div key={feature} className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-muted-foreground">{feature}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {isComingSoon ? (
                      product.page_url ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(product.page_url); }}
                          className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 mt-4 shadow-md hover:shadow-lg"
                          data-testid={`learn-more-${index}`}
                        >
                          Learn More — Coming Soon
                        </button>
                      ) : (
                        <button
                          disabled
                          className="w-full bg-gray-100 text-gray-500 font-semibold py-4 px-6 rounded-xl cursor-not-allowed mt-4"
                          data-testid={`coming-soon-btn-${index}`}
                        >
                          Coming Soon
                        </button>
                      )
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/products/${product.id}`);
                        }}
                        className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 mt-4 shadow-md hover:shadow-lg"
                        data-testid={`shop-now-${index}`}
                      >
                        Shop Now
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
              );
            })}
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-16 text-center bg-gradient-to-br from-primary/5 to-white rounded-3xl p-12"
        >
          <h3 className="text-2xl font-bold mb-4 font-heading">Order Today, Direct From Us</h3>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            IIY BCM-95® is now available to purchase online. Free UK delivery via Royal Mail 48. Volume discounts on multi-bottle and wholesale orders.
          </p>
          <button
            onClick={() => navigate('/products')}
            className="bg-primary hover:bg-primary/90 text-white font-semibold py-4 px-8 rounded-xl transition-all duration-300 hover:-translate-y-1 shadow-lg"
            data-testid="products-shop-cta"
          >
            Shop BCM-95®
          </button>
        </motion.div>
      </div>
    </div>
  );
}